/**
 * Pure rules behind the panel's motion. The values match the tokens in
 * styles/motion.css; the components only read these answers.
 */

export type NavDirection = 'forward' | 'back' | 'none';

/** Where a path sits in the nav order: the longest entry it starts with, or -1. */
export function navIndex(order: readonly string[], pathname: string): number {
  let best = -1;
  let bestLength = -1;
  order.forEach((entry, index) => {
    const matches = entry === '/' ? pathname === '/' : pathname === entry || pathname.startsWith(`${entry}/`);
    if (matches && entry.length > bestLength) {
      best = index;
      bestLength = entry.length;
    }
  });
  return best;
}

/** Which way a screen change travels along the tabs, so the new screen can lean in from that side. */
export function navDirection(order: readonly string[], from: string | null, to: string): NavDirection {
  if (from === null) return 'none';
  const before = navIndex(order, from);
  const after = navIndex(order, to);
  if (before < 0 || after < 0 || before === after) return 'none';
  return after > before ? 'forward' : 'back';
}

/** Only a new path is a new screen: search and hash changes keep the page still. */
export function isNewScreen(from: string | null, to: string): boolean {
  return from !== to;
}

export const STAGGER_MS = 35;
export const STAGGER_CAP = 8;

/** How long an item in a list waits before it enters: 35 ms per item, never more than eight steps. */
export function staggerDelay(index: number): number {
  return Math.min(Math.max(index, 0), STAGGER_CAP) * STAGGER_MS;
}

/** A flick faster than this (px per ms) closes a sheet whatever its distance. */
export const SHEET_DISMISS_VELOCITY = 0.11;
/** A slow drag closes the sheet once it passes this share of its size. */
export const SHEET_DISMISS_FRACTION = 0.3;

export function shouldDismissSheet({ distance, elapsedMs, size }: { distance: number; elapsedMs: number; size: number }): boolean {
  if (distance <= 0) return false;
  const velocity = distance / Math.max(elapsedMs, 1);
  return velocity > SHEET_DISMISS_VELOCITY || distance > size * SHEET_DISMISS_FRACTION;
}

export type Presence = 'closed' | 'opening' | 'open' | 'closing';
export type PresenceEvent = 'show' | 'hide' | 'settled';

/** The life of an overlay: it stays mounted while it closes, and can turn around mid-way. */
export function nextPresence(state: Presence, event: PresenceEvent): Presence {
  switch (event) {
    case 'show':
      return state === 'closed' || state === 'closing' ? 'opening' : state;
    case 'hide':
      return state === 'open' || state === 'opening' ? 'closing' : state;
    case 'settled':
      if (state === 'opening') return 'open';
      if (state === 'closing') return 'closed';
      return state;
  }
}

/** The indicator's box over the active item, in the container's coordinates. */
export function indicatorBox(
  container: { left: number; top: number },
  item: { left: number; top: number; width: number; height: number },
): { x: number; y: number; w: number; h: number } {
  return { x: item.left - container.left, y: item.top - container.top, w: item.width, h: item.height };
}

/** Whether the person asked the system for less motion. */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
